import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { DatePipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  resource,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";

import { StatsService } from "../../../apis/networkdashboard/rest/services";
import { ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { MonthNamePipe } from "../../../helpers/datehelper/monthname";
import { EMPTY_SLS_DATA } from "./types";

@Component({
  selector: "sla-report",
  templateUrl: "sla-report.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, DatePipe, FormsModule, MonthNamePipe, ReadableSizePipe, TranslatePipe],
})
export class SLAReport {
  private stats = inject(StatsService);
  private translate = inject(TranslateService);

  readonly subscriptionId = input.required<string>();

  protected dataError = signal("");
  protected dataAvailable = computed(() => this.dataError() === "");

  protected year = signal(new Date(new Date().setDate(0)).getFullYear());
  protected slsOffset = 0.3;

  private readonly slsResource = resource({
    params: () => ({ subscription_id: this.subscriptionId(), year: this.year(), month: this.month() + 1 }),
    loader: ({ params }) =>
      this.stats
        .getSubscriptionSls(params)
        .then((data) => {
          this.dataError.set("");
          return data;
        })
        .catch((err: { status?: number }) => {
          this.dataError.set(this.translate.instant(err.status === 404 ? "SLA.Report.NoData" : "SLA.Report.LoadError"));
          return undefined;
        }),
  });

  protected readonly fetching = this.slsResource.isLoading;
  protected readonly slsData = computed(() => this.slsResource.value() ?? EMPTY_SLS_DATA);

  protected readonly firstMonth = computed(() => {
    const d = new Date(this.slsData().startDateSubscription + "T12:00:00Z");
    d.setMonth(d.getMonth() + 1);
    return this.year() === d.getFullYear() ? d.getMonth() : 0;
  });

  protected readonly lastMonth = computed(() => {
    const d = new Date(new Date().setDate(0));
    return this.year() === d.getFullYear() ? d.getMonth() : 11;
  });

  protected month = linkedSignal<{ last: number; isCurrentYear: boolean }, number>({
    source: () => ({
      last: this.lastMonth(),
      isCurrentYear: this.year() === new Date(new Date().setDate(0)).getFullYear(),
    }),
    computation: ({ last, isCurrentYear }, previous) => {
      if (isCurrentYear) return last;
      const current = previous?.value ?? last;
      return Math.min(Math.max(current, 0), last);
    },
  });

  protected readonly availableYears = computed(() => {
    const d = new Date(this.slsData().startDateSubscription + "T12:00:00Z");
    d.setMonth(d.getMonth() + 1);
    const firstYear = d.getFullYear();
    const thisYear = new Date(new Date().setDate(0)).getFullYear();
    return Array.from({ length: thisYear - firstYear + 1 }, (_, i) => firstYear + i);
  });

  protected updateMonth(value: string): void {
    this.month.set(parseInt(value));
  }

  protected updateYear(value: string): void {
    this.year.set(parseInt(value));
  }
}
